import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../../lib/api.js';
import { useDocHistory } from '../../lib/use-doc-history.js';
import { eventSocket } from '../../lib/ws.js';
import { logEditor } from '../../state/console.js';
import { useWorkspace } from '../../state/workspace.js';
import { FieldEditor } from '../scene/Inspector.js';
import { Card, PropRow } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import type { SettingsModule, SettingsSection, SettingsSectionProps } from './settings-types.js';
import { issuesAt, validateSettings, type SettingsIssue } from './settings-validate.js';
import './settings.css';

/**
 * Project settings (`prism.project.json` → `settings.*`), one card per
 * section file in ./sections/. Edits are written back to the manifest
 * (debounced); every other key of the file is preserved. Every edit is a
 * labelled undo step (shared undo service, Scene view) and problems that
 * `prism check` would report are shown inline.
 */

const MODULES = import.meta.glob<SettingsModule>('./sections/*.ts', { eager: true });
export const SETTINGS_SECTIONS: SettingsSection[] = Object.values(MODULES)
  .flatMap((m) => (Array.isArray(m.default) ? m.default : [m.default]))
  .sort((a, b) => (a.order ?? 100) - (b.order ?? 100));

const MANIFEST = 'prism.project.json';
type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => v !== null && typeof v === 'object' && !Array.isArray(v);

function IssueList({ issues }: { issues: readonly SettingsIssue[] }) {
  if (issues.length === 0) return null;
  return (
    <>
      {issues.map((issue, i) => (
        <div key={i} className={`settings-issue is-${issue.level}`}>
          <Icon name={issue.level === 'error' ? 'error' : 'warning'} size={12} />
          <span>
            <span className="mono">settings.{issue.path}</span> {issue.message}
          </span>
        </div>
      ))}
    </>
  );
}

export function ProjectSettings() {
  const projectId = useWorkspace((s) => s.projectId);
  const [doc, setDoc] = useState<Obj | null>(null);
  const docRef = useRef<Obj | null>(null);
  docRef.current = doc;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<Obj | null>(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await api.get<{ file: { content: string } }>(`/projects/${projectId}/file?path=${MANIFEST}`);
      const parsed = JSON.parse(res.file.content) as unknown;
      setDoc(isObj(parsed) ? parsed : null);
    } catch {
      setDoc(null); // Not a Prism Engine Cloud project, or a broken manifest (prism check reports it).
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Reload after outside edits (AI, code editor) unless our own write is pending.
  useEffect(
    () =>
      eventSocket.subscribe((event) => {
        if (event.type === 'file.changed' && event.path === MANIFEST && !pending.current) void load();
      }),
    [load],
  );

  const flush = useCallback(async () => {
    const next = pending.current;
    if (!next || !projectId) return;
    try {
      await api.put(`/projects/${projectId}/file`, { path: MANIFEST, content: `${JSON.stringify(next, null, 2)}\n` });
    } catch (error) {
      logEditor('error', `Saving project settings failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      if (pending.current === next) pending.current = null;
    }
  }, [projectId]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      void flush();
    },
    [flush],
  );

  /** Show and save a document state (edits, undo, redo). */
  const commit = useCallback(
    (next: Obj) => {
      docRef.current = next;
      setDoc(next);
      pending.current = next;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void flush(), 400);
    },
    [flush],
  );

  const history = useDocHistory<Obj | null>({
    id: `project-settings:${projectId ?? 'none'}`,
    label: 'Project Settings',
    view: 'scene',
    read: () => docRef.current,
    apply: (state) => {
      if (state) commit(state);
    },
  });

  /** One labelled undo step: `patch` replaces `settings` keys (undefined removes one). */
  const update = useCallback(
    (patch: Obj, label: string, mergeKey?: string) => {
      const before = docRef.current;
      if (!before) return;
      const nextSettings: Obj = { ...(isObj(before.settings) ? before.settings : {}) };
      for (const [key, value] of Object.entries(patch)) {
        if (value === undefined) delete nextSettings[key];
        else nextSettings[key] = value;
      }
      history.record(before, label, mergeKey);
      commit({ ...before, settings: nextSettings });
    },
    [commit, history],
  );

  const settings = doc && isObj(doc.settings) ? doc.settings : null;
  const issues = useMemo(() => (settings ? validateSettings(settings) : []), [settings]);
  if (!doc || !settings) return doc ? <ProjectSettingsBody settings={{}} issues={[]} update={update} /> : null;
  return <ProjectSettingsBody settings={settings} issues={issues} update={update} />;
}

function ProjectSettingsBody({
  settings,
  issues,
  update,
}: {
  settings: Obj;
  issues: SettingsIssue[];
  update: (patch: Obj, label: string, mergeKey?: string) => void;
}) {
  const setField = (section: SettingsSection, key: string, value: unknown, label: string) => {
    const current = isObj(settings[section.key]) ? { ...(settings[section.key] as Obj) } : {};
    if (value === undefined) delete current[key];
    else current[key] = value;
    update(
      { [section.key]: Object.keys(current).length > 0 ? current : undefined },
      `Set ${section.label} › ${label}`,
      `${section.key}.${key}`,
    );
  };

  return (
    <>
      {SETTINGS_SECTIONS.map((section) => {
        const raw = settings[section.key];
        const value = isObj(raw) ? raw : {};
        const sectionIssues = [section.key, ...(section.issueKeys ?? [])].flatMap((key) => issuesAt(issues, key));
        // Issues of the section's typed fields show under the field; the rest at the top.
        const fieldIssues = (key: string) => issuesAt(sectionIssues, `${section.key}.${key}`);
        const loose = section.render
          ? []
          : sectionIssues.filter((i) => !section.fields.some((f) => issuesAt([i], `${section.key}.${f.key}`).length > 0));
        const errors = sectionIssues.filter((i) => i.level === 'error').length;
        const Custom = section.render;
        const props: SettingsSectionProps = {
          section,
          value: raw,
          settings,
          set: (v, label) => update({ [section.key]: v }, label),
          update,
          issues: sectionIssues,
        };
        return (
          <Card
            key={`${section.key}:${section.label}`}
            title={section.label}
            icon={section.icon}
            subtitle={section.hint}
            defaultOpen={false}
            actions={
              sectionIssues.length > 0 ? (
                <span
                  className={`settings-issue-badge ${errors > 0 ? 'is-error' : 'is-warning'}`}
                  title={sectionIssues.map((i) => `settings.${i.path} ${i.message}`).join('\n')}
                >
                  <Icon name={errors > 0 ? 'error' : 'warning'} size={12} />
                  {sectionIssues.length}
                </span>
              ) : undefined
            }
          >
            <IssueList issues={loose} />
            {Custom && section.renderAt === 'start' && <Custom {...props} />}
            {section.fields
              .filter((field) => !field.showIf || field.showIf(value))
              .map((field) => (
                <div key={field.key} className="settings-field">
                  <PropRow label={field.label} hint={field.hint ?? `settings.${section.key}.${field.key}`}>
                    <FieldEditor
                      def={field}
                      value={value[field.key] ?? field.read?.(value)}
                      onChange={(v) => setField(section, field.key, v, field.label)}
                    />
                  </PropRow>
                  <IssueList issues={fieldIssues(field.key)} />
                </div>
              ))}
            {Custom && section.renderAt !== 'start' && <Custom {...props} />}
            {Custom && <IssueList issues={sectionIssues.filter((i) => !section.fields.some((f) => fieldIssues(f.key).includes(i)))} />}
          </Card>
        );
      })}
    </>
  );
}
